import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@/components/ui/chart';
import { ProjectChapterStats } from '@/services/progress-tracker/api';
import {
  BookOpen,
  PieChart,
  RefreshCw,
  TrendingUp,
  Type
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis
} from 'recharts';

interface WordCountTabProps {
  projectId: string;
  chapterStats: ProjectChapterStats[];
  isLoading: boolean;
  onRefresh: () => void;
  availableStories?: Array<{ id: number; name: string }>;
}

// Chart configuration
const chartConfig = {
  words: {
    label: "Words",
    color: "hsl(var(--primary))",
  },
} satisfies ChartConfig;

const WordCountTab: React.FC<WordCountTabProps> = ({
  projectId,
  chapterStats,
  isLoading,
  onRefresh,
  availableStories = [],
}) => {
  const [selectedStoryId, setSelectedStoryId] = useState<string | 'all'>('all');

  // Filter stats based on selection
  const filteredStats = useMemo(() => {
    if (selectedStoryId === 'all') return chapterStats;
    return chapterStats.filter(stat => stat.storyId.toString() === selectedStoryId);
  }, [chapterStats, selectedStoryId]);

  // Calculate Metrics
  const metrics = useMemo(() => {
    const totalWords = filteredStats.reduce((sum, item) => sum + item.wordCount, 0);
    const totalChars = filteredStats.reduce((sum, item) => sum + item.characterCount, 0);
    const totalChapters = filteredStats.length;
    const avgWords = totalChapters > 0 ? Math.round(totalWords / totalChapters) : 0;

    // Find largest chapter
    const largestChapter = filteredStats.reduce((max, item) =>
      item.wordCount > (max?.wordCount || 0) ? item : max
      , null as ProjectChapterStats | null);

    return { totalWords, totalChars, totalChapters, avgWords, largestChapter };
  }, [filteredStats]);

  const chartData = useMemo(() => {
    return filteredStats.map(stat => ({
      name: stat.chapterTitle || `Chapter ${stat.chapterId}`,
      words: stat.wordCount
    }));
  }, [filteredStats]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!chapterStats || chapterStats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted p-4 mb-4">
          <PieChart className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
        <p className="text-muted-foreground max-w-sm mb-6">
          Start writing in your chapters to see analytics data.
        </p>
        <Button onClick={onRefresh} variant="outline" size="sm">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh Data
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold tracking-tight">Analytics</h2>
          <Select value={selectedStoryId} onValueChange={setSelectedStoryId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Stories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stories</SelectItem>
              {availableStories.map(story => (
                <SelectItem key={story.id} value={story.id.toString()}>{story.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh} className="text-muted-foreground hover:text-foreground">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Words</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalWords.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Across {metrics.totalChapters} chapters</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Characters</CardTitle>
            <Type className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalChars.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Including spaces</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Chapter Length</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.avgWords.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Words per chapter</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Largest Chapter</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold truncate" title={metrics.largestChapter?.chapterTitle}>
              {metrics.largestChapter?.chapterTitle || '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              {metrics.largestChapter?.wordCount.toLocaleString() || 0} words
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Chart */}
      <Card className="col-span-4">
        <CardHeader>
          <CardTitle>Word Count Distribution</CardTitle>
          <CardDescription>
            Word count breakdown per chapter
          </CardDescription>
        </CardHeader>
        <CardContent className="pl-2">
          <ChartContainer config={chartConfig} className="h-[350px] w-full">
            <BarChart data={chartData}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                minTickGap={32}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}`}
              />
              <ChartTooltip
                cursor={{ fill: 'var(--muted)', opacity: 0.2 }}
                content={<ChartTooltipContent indicator="dashed" />}
              />
              <Bar
                dataKey="words"
                fill="var(--color-words)"
                radius={[4, 4, 0, 0]}
                barSize={40}
              />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};
export default WordCountTab;
